import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty.js';
import { styled } from '@linaria/react';
import { AlertCard, Button } from '@livecommerce/design-system';

import { deleteFooterNotification as deleteFooterNotificationAction } from 'actions/notifications.js';
import useAutoHide from './useAutoHide.js';

const PositionContainer = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 2;
  width: 376px;
`;

const TYPES_MAP = {
  error: 'danger',
  warning: 'warning',
  default: 'feedback',
};

const getActions = ({ action, variant, handleClick, actionText }) => {
  if (!action) return null;

  const buttonTheme = variant === 'feedback' || variant === 'danger' ? 'dark' : 'light';

  return (
    <Button theme={buttonTheme} variant="solid" onClick={handleClick}>
      {actionText}
    </Button>
  );
};

const NotificationFooter = () => {
  const dispatch = useDispatch();
  const deleteFooterNotification = useCallback(() => dispatch(deleteFooterNotificationAction()), [dispatch]);
  const footer = useSelector((state) => state.notifications.footer || {});

  const {
    title = null,
    message = null,
    type = 'default',
    action = null,
    actionText = null,
    dataTestId = null,
    autoHide = true,
    delayedMessage = false,
    actionOnClose = null,
  } = footer;

  const onAutoHide = useCallback(() => {
    actionOnClose?.();
    deleteFooterNotification();
  }, [deleteFooterNotification]);

  const { autoHideTimer, delayedMessageTimer, showMessage } = useAutoHide(
    {
      autoHide,
      action,
      type,
      delayedMessage,
      message,
    },
    onAutoHide,
  );

  if (isEmpty(footer)) return null;

  const handleClose = () => {
    actionOnClose?.();
    deleteFooterNotification();
  };

  const handleAction = () => {
    action();
    deleteFooterNotification();
    if (autoHideTimer.current) clearTimeout(autoHideTimer.current);
    if (delayedMessageTimer.current) clearTimeout(delayedMessageTimer.current);
  };

  const variant = TYPES_MAP[type] || 'feedback';

  const actions = getActions({
    action,
    variant,
    handleClick: handleAction,
    actionText,
  });

  return (
    <PositionContainer>
      <AlertCard
        variant={variant}
        title={title}
        isCloseButtonShown
        actions={actions}
        dataTestId={dataTestId}
        onClose={handleClose}
      >
        {showMessage && message}
      </AlertCard>
    </PositionContainer>
  );
};

export default NotificationFooter;
